import { useId, type ReactNode } from "react";
import { SelectionIndicator } from "./SelectionIndicator";

/**
 * Accessible segmented control — a single-select pill group rendered as a
 * radiogroup. Use for short, mutually-exclusive choices (currency, billing
 * cadence, product family) where a `<Select>` would feel heavy. Never styled
 * gold; the primary screen action stays the one gold button.
 */
export interface SegmentedOption<T extends string> {
  value: T;
  label: ReactNode;
  icon?: ReactNode;
  disabled?: boolean;
}

export interface SegmentedProps<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  size?: "sm" | "md";
  ariaLabel?: string;
  className?: string;
}

export function Segmented<T extends string>({
  options,
  value,
  onChange,
  size = "md",
  ariaLabel,
  className,
}: SegmentedProps<T>) {
  const indicator = useId();
  const cls = ["fd-segmented", "fd-segmented--motion", size === "sm" && "fd-segmented--sm", className]
    .filter(Boolean)
    .join(" ");
  return (
    <div className={cls} role="radiogroup" aria-label={ariaLabel}>
      {options.map((opt) => {
        const active = opt.value === value;
        return (
          <button
            key={opt.value}
            type="button"
            role="radio"
            aria-checked={active}
            tabIndex={active || (!options.some(o => o.value === value && !o.disabled) && options.find(o => !o.disabled)?.value === opt.value) ? 0 : -1}
            disabled={opt.disabled}
            className={["fd-segmented__item", active && "fd-segmented__item--active"]
              .filter(Boolean)
              .join(" ")}
            onClick={() => onChange(opt.value)}
            onKeyDown={event => {
              if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(event.key)) return;
              event.preventDefault();
              const enabled = options.filter(o => !o.disabled), index = enabled.findIndex(o => o.value === opt.value);
              const target = event.key === 'Home' ? 0 : event.key === 'End' ? enabled.length - 1 : (index + (['ArrowRight', 'ArrowDown'].includes(event.key) ? 1 : -1) + enabled.length) % enabled.length;
              const next = enabled[target]; if (!next) return;
              event.currentTarget.parentElement?.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')[target]?.focus();
              onChange(next.value);
            }}
          >
            {active && <SelectionIndicator id={indicator} />}
            <span className="fd-choice-label">{opt.icon}{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}
